import { Navigate, useParams } from "react-router-dom";
import EntityProvider from "pages/components/Behavior/EntityProvider";
import * as routeConfig from "Infrastructure/config/routesConfig.js";

import {
  displaySessionsSection,
  getPaymentPlan,
  getPaymentPlanPublic,
  sendPaymentPlanToDebtor,
  triggerNextSession,
} from "Application/PaymentPlan/PaymentPlanService";
import {
  PaymentPlan,
  PaymentPlanEntity,
} from "Domain/PaymentPlan/Entity/PaymentPlan";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import DisplayDebtor from "./Parts/DisplayDebtor";
import DisplayPaymentPlan from "./Parts/DisplayPaymentPlan";
import DisplayPaymentsInDetails from "./Parts/DisplayPaymentsInDetails";

import Button from "pages/components/FormFields/Button";
import { useTranslation } from "react-i18next";
import StatusProgress from "pages/components/Molecules/StatusProgress";
import {
  EXPAND_PAYMENT_PLAN_CONTRACT,
  TRIGGER_DIALOG,
} from "pages/constants/EventKeys";
import { eventDispatcher } from "pages/Hooks/useEventListener";
import { pay, payPublic } from "Application/PaymentPlan/PaymentProviderService";
import DisplayPaymentsOutDetails from "./Parts/DisplayPaymentsOutDetails";
import { getCurrentUserData } from "Application/User/UserService";

type PaymentPlanDetailsProps = {
  publicAccess?: boolean;
};

export default function PaymentPlanDetails({
  publicAccess,
}: PaymentPlanDetailsProps) {
  const { id = "", token = "" } = useParams<string>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const paymentPlanGetter = publicAccess
    ? getPaymentPlanPublic
    : getPaymentPlan;
  const paymentPlanParam = publicAccess ? token : id;

  return (
    <EntityProvider<PaymentPlan>
      callback={paymentPlanGetter}
      params={[paymentPlanParam]}
      {...(token && {
        notFoundComponent: (
          <Navigate to={routeConfig.getRoute(routeConfig.LOGIN)} />
        ),
      })}
    >
      {(paymentPlan, setPaymentPlan) => (
        <div>
          <Box display={"flex"} justifyContent={"end"} paddingBottom={1}>
            <SessionNextStep
              paymentPlan={paymentPlan}
              updatePaymentPlan={setPaymentPlan}
            />
          </Box>

          <PaymentPlanState paymentPlan={paymentPlan} token={token} />
          <Box display={"grid"} gap={2} width={"100%"}>
            <Box
              display={"grid"}
              gridTemplateColumns={isMobile ? "1fr" : "1.5fr 4fr"}
              gap={2}
              width={"100%"}
            >
              <DisplayDebtor debtor={paymentPlan.debtorProfile} update={true} />
              <DisplayPaymentPlan
                paymentPlan={paymentPlan}
                refreshPaymentPlan={setPaymentPlan}
                publicToken={token}
              />
            </Box>

            <Box
              display={"flex"}
              {...(isMobile && {
                flexDirection: "column",
              })}
              gap={2}
              width={"100%"}
            >
              <Box height={"fit-content"} width={"100%"}>
                <DisplayPaymentsInDetails paymentsIn={paymentPlan.paymentsIn} />
              </Box>

              {paymentPlan.paymentsOut &&
                paymentPlan.paymentsOut.length > 0 && (
                  <Box height={"fit-content"} width={"100%"}>
                    <DisplayPaymentsOutDetails
                      paymentsOut={paymentPlan.paymentsOut}
                    />
                  </Box>
                )}
            </Box>
          </Box>
        </div>
      )}
    </EntityProvider>
  );
}

type PaymentPlanStateProps = {
  paymentPlan: PaymentPlan;
  token?: string;
};

function PaymentPlanState({ paymentPlan, token }: PaymentPlanStateProps) {
  const currentUser = getCurrentUserData();
  const paymentPlanEntity = new PaymentPlanEntity(paymentPlan);
  const { t } = useTranslation();
  if (
    paymentPlanEntity.isContractSigned() &&
    paymentPlanEntity.isPaymentProcessStarted()
  ) {
    return <></>;
  }
  function expandPaymentPlanContract() {
    eventDispatcher(EXPAND_PAYMENT_PLAN_CONTRACT);
  }

  function proceedToPayment() {
    if (token) {
      payPublic(token);
    } else {
      pay(paymentPlan.publicId);
    }
  }
  function sendToDebtor() {
    eventDispatcher(
      TRIGGER_DIALOG,
      getSendToDebtorDialogContent(paymentPlan.publicId)
    );
  }

  let currentStep = 0;
  if (!paymentPlanEntity.isContractSigned()) {
    currentStep = 1;
  } else {
    currentStep = 2;
  }
  const steps = [
    {
      label: t("STEPS.payment_plan_creation.label"),
      text: t("STEPS.payment_plan_creation.text"),
    },
    {
      label: (
        <Button
          label={t("STEPS.payment_plan_contract.label")}
          size="small"
          onClick={expandPaymentPlanContract}
          disabled={currentStep !== 1}
        />
      ),
      text: t("STEPS.payment_plan_contract.text"),
    },
    {
      label: (
        <Button
          label={t("STEPS.payment_plan_payment.label")}
          size="small"
          onClick={proceedToPayment}
          disabled={currentStep !== 2}
        />
      ),
      text: t("STEPS.payment_plan_payment.text"),
    },
  ];

  return (
    <StatusProgress
      title={t("STEPS.title_payment_state")}
      {...(!token &&
        !currentUser.isUserDebtor() && {
          action: (
            <Button
              onClick={sendToDebtor}
              label={t("CONTENT.send_to_debtor")}
              size="small"
              variant="outlined"
            />
          ),
        })}
      steps={steps}
      currentStep={currentStep}
    />
  );
}

function getSendToDebtorDialogContent(paymentPlanPublicId: string) {
  function handleClick() {
    sendPaymentPlanToDebtor(paymentPlanPublicId);
  }
  const actions = [
    {
      label: "confirm",
      variant: "outlined",
      color: "success",
      action: handleClick,
    },
    {
      label: "cancel",
      variant: "outlined",
      color: "error",
    },
  ];
  return {
    type: "payment_plan_send_to_debtor",
    actions: actions,
  };
}

type SessionNextStepProps = {
  paymentPlan: PaymentPlan;
  updatePaymentPlan: Function;
};

function SessionNextStep({
  paymentPlan,
  updatePaymentPlan,
}: SessionNextStepProps) {
  const { t } = useTranslation();
  const displaySession = displaySessionsSection(paymentPlan);

  if (!displaySession.display) {
    return <></>;
  }

  function triggerDialog() {
    function handleTriggerNextStep() {
      triggerNextSession(paymentPlan.publicId).then((res: any) => {
        if (res && !("error" in res)) {
          updatePaymentPlan(res);
        }
      });
    }
    const actions = [
      {
        label: "confirm",
        variant: "outlined",
        color: "success",
        action: handleTriggerNextStep,
      },
      {
        label: "cancel",
        variant: "outlined",
        color: "error",
      },
    ];
    eventDispatcher(TRIGGER_DIALOG, {
      type: "confirm_secured_session",
      typeParams: { session: displaySession.nextSession },
      actions: actions,
    });
  }

  return (
    <>
      <Button
        label={t(`FORM.SUBMIT.confirm_session`, {
          session: displaySession.nextSession,
        })}
        onClick={triggerDialog}
        disabled={!displaySession.isActive}
      />
    </>
  );
}
